import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut, Loader2, Phone, Car, Calendar as CalendarIcon2, Clock, CheckCircle2,
  Check, X, Trash2, ClipboardList, Plus, DollarSign, TrendingUp, CalendarClock,
  LayoutDashboard, Users, Columns3, Bell,
  WalletCards, Calculator,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { format } from "date-fns";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { SERVICE_OPTIONS } from "../data";
import { startGoogleLogin } from "../lib/auth";
import { CrmWorkspace } from "../components/crm/CrmWorkspace";
import { FinanceWorkspace } from "../components/crm/FinanceWorkspace";
import { AppointmentFinanceDrawer } from "../components/crm/AppointmentFinanceDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "marez_admin_token";

const FILTERS = ["all", "new", "confirmed", "completed", "cancelled"];
const ADMIN_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "appointments", label: "Appointments", icon: CalendarIcon2 },
  { id: "customers", label: "Customers", icon: Users },
  { id: "pipeline", label: "Pipeline", icon: Columns3 },
  { id: "reminders", label: "Reminders", icon: Bell },
  { id: "finance", label: "Finance", icon: WalletCards },
];

const STATUS_STYLES = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      toast.success("Welcome back, " + res.data.user.name);
      onLogin(res.data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0a0a0a] border-white/10 text-white placeholder:text-zinc-600 focus:border-white focus-visible:ring-1 focus-visible:ring-white h-12";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#111111] p-8"
      >
        <div className="text-center mb-8">
          <p className="font-heading text-3xl font-extrabold tracking-tighter text-white">MAREZ</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">
            Admin Dashboard
          </p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Email</Label>
            <Input
              data-testid="admin-email"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@marezdetailing.com"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Password</Label>
            <Input
              data-testid="admin-password"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="btn-3d w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-6 py-3.5 hover:bg-white disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="h-px bg-white/10 flex-1" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">or</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <button
            data-testid="admin-google-btn"
            type="button"
            onClick={() => startGoogleLogin("/admin")}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-medium px-6 py-3.5 hover:bg-white/5 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.3-1.68 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.3 14.7 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12S6.6 21.7 12 21.7c6.9 0 9.6-4.8 9.6-7.3 0-.5 0-.9-.1-1.3H12z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, confirmed: 0, completed: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [financeBooking, setFinanceBooking] = useState(null);

  const authAxios = useCallback(
    () => axios.create({
      baseURL: API,
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const client = authAxios();
      const [b, s, a] = await Promise.all([
        client.get("/admin/bookings", { params: { status: filter } }),
        client.get("/admin/stats"),
        client.get("/admin/analytics"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setAnalytics(a.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [authAxios, filter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await authAxios().patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    try {
      await authAxios().delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const statCards = [
    { label: "Total", value: stats.total, key: "total" },
    { label: "New", value: stats.new, key: "new" },
    { label: "Confirmed", value: stats.confirmed, key: "confirmed" },
    { label: "Completed", value: stats.completed, key: "completed" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] pb-20">
      <header className="border-b border-white/10 bg-[#0a0a0a] sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-heading text-xl font-extrabold tracking-tighter text-white">
              MAREZ <span className="text-zinc-500 font-normal text-sm">Admin</span>
            </p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 px-4 py-2 text-sm transition-colors"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
        <nav data-testid="admin-navigation" className="max-w-[1500px] mx-auto px-5 md:px-8 overflow-x-auto">
          <div className="flex min-w-max gap-1">
            {ADMIN_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`admin-nav-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative inline-flex items-center gap-2 px-4 py-3 text-xs font-medium transition-colors ${activeTab === tab.id ? "text-white" : "text-zinc-500 hover:text-zinc-200"}`}
                >
                  <Icon size={14} /> {tab.label}
                  {activeTab === tab.id && <motion.span layoutId="admin-tab-line" className="absolute left-3 right-3 bottom-0 h-px bg-white" />}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      {activeTab === "finance" ? (
        <FinanceWorkspace authAxios={authAxios} />
      ) : (["customers", "pipeline", "reminders"].includes(activeTab)) ? (
        <CrmWorkspace view={activeTab} authAxios={authAxios} />
      ) : (
      <div className="max-w-6xl mx-auto px-6 pt-8">
        {activeTab === "overview" && <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div
              key={s.key}
              data-testid={`stat-${s.key}`}
              className="rounded-2xl border border-white/10 bg-[#111111] p-5"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {s.label}
              </p>
              <p className="font-heading text-3xl font-bold text-white mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Analytics */}
        {analytics && (
          <div data-testid="analytics-section" className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 bg-[#111111] p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <DollarSign size={19} className="text-emerald-300" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Revenue Estimate
                </p>
                <p data-testid="analytics-revenue" className="font-heading text-2xl font-bold text-white mt-1">
                  ${analytics.revenue_estimate.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#111111] p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-full bg-amber-500/15 flex items-center justify-center">
                <CalendarClock size={19} className="text-amber-300" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Upcoming (7 days)
                </p>
                <p data-testid="analytics-upcoming" className="font-heading text-2xl font-bold text-white mt-1">
                  {analytics.upcoming_7_days}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#111111] p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-full bg-blue-500/15 flex items-center justify-center">
                <TrendingUp size={19} className="text-blue-300" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Total Bookings
                </p>
                <p className="font-heading text-2xl font-bold text-white mt-1">
                  {analytics.total_bookings}
                </p>
              </div>
            </div>
          </div>
        )}

        {analytics && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 bg-[#111111] p-5 lg:col-span-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Bookings — Last 30 Days
              </p>
              <div className="h-56 min-w-0" data-testid="chart-daily">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={100}>
                  <BarChart data={analytics.daily_bookings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                    <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#71717a" fontSize={10} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, color: "#fff" }}
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    />
                    <Bar dataKey="count" fill="#fafafa" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#111111] p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Top Services
              </p>
              <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {analytics.top_services.length === 0 ? (
                  <p className="text-sm text-zinc-600">No bookings yet.</p>
                ) : (
                  analytics.top_services.map((s) => {
                    const max = analytics.top_services[0].count || 1;
                    const pct = (s.count / max) * 100;
                    return (
                      <div key={s.service}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-300 truncate">{s.service || "Unspecified"}</span>
                          <span className="text-zinc-500">{s.count}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full bg-white/60" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
        </>}

        {activeTab === "appointments" && <>
        {/* Filters + Add */}
        <div className="mt-8 flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 text-sm capitalize transition-colors border ${
                  filter === f
                    ? "bg-[#fafafa] text-[#050505] border-white"
                    : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="add-appointment-btn"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-4 py-2 text-sm hover:bg-white"
          >
            <Plus size={15} /> Add Appointment
          </button>
        </div>

        {/* Bookings */}
        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
              <Loader2 className="animate-spin" size={20} /> Loading...
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <ClipboardList size={40} className="mx-auto mb-4 opacity-50" />
              No bookings in this view.
            </div>
          ) : (
            bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="rounded-2xl border border-white/10 bg-[#111111] p-5 md:p-6"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-semibold text-white">{b.name}</h3>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLES[b.status] || ""}`}>
                        {b.status}
                      </span>
                      <span className="font-mono text-[11px] text-zinc-500">
                        {b.confirmation_number}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-400">
                      <span className="flex items-center gap-2">
                        <Phone size={14} /> <a href={`tel:${b.phone}`} className="hover:text-white">{b.phone}</a>
                      </span>
                      <span className="flex items-center gap-2">
                        <Car size={14} /> {[b.vehicle_year, b.vehicle_make, b.vehicle_model].filter(Boolean).join(" ") || "—"}
                      </span>
                      <span className="flex items-center gap-2">
                        <CheckCircle2 size={14} /> {b.service}{b.condition ? ` · ${b.condition}` : ""}
                      </span>
                      <span className="flex items-center gap-2">
                        <CalendarIcon2 size={14} /> {b.preferred_date}
                        <Clock size={14} className="ml-2" /> {b.preferred_time}
                      </span>
                    </div>
                    {b.notes && (
                      <p className="mt-3 text-sm text-zinc-500 italic border-l-2 border-white/10 pl-3">
                        {b.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex md:flex-col gap-2 shrink-0">
                    {b.status === "completed" && (
                      <button
                        data-testid={`action-finance-${b.id}`}
                        onClick={() => setFinanceBooking(b)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-500/15 text-blue-300 border border-blue-500/30 px-3 py-1.5 text-xs hover:bg-blue-500/25 transition-colors"
                      >
                        <Calculator size={13} /> Revenue
                      </button>
                    )}
                    {b.status !== "confirmed" && b.status !== "completed" && (
                      <button
                        data-testid={`action-confirm-${b.id}`}
                        onClick={() => updateStatus(b.id, "confirmed")}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 px-3 py-1.5 text-xs hover:bg-amber-500/25 transition-colors"
                      >
                        <Check size={13} /> Confirm
                      </button>
                    )}
                    {b.status !== "completed" && (
                      <button
                        data-testid={`action-complete-${b.id}`}
                        onClick={() => updateStatus(b.id, "completed")}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 text-xs hover:bg-emerald-500/25 transition-colors"
                      >
                        <CheckCircle2 size={13} /> Complete
                      </button>
                    )}
                    {b.status !== "cancelled" && (
                      <button
                        data-testid={`action-cancel-${b.id}`}
                        onClick={() => updateStatus(b.id, "cancelled")}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 px-3 py-1.5 text-xs hover:bg-red-500/25 transition-colors"
                      >
                        <X size={13} /> Cancel
                      </button>
                    )}
                    <button
                      data-testid={`action-delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 text-zinc-500 px-3 py-1.5 text-xs hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </>}
      </div>
      )}

      <AddAppointmentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        authAxios={authAxios}
        onCreated={load}
      />
      <AppointmentFinanceDrawer
        booking={financeBooking}
        open={Boolean(financeBooking)}
        onOpenChange={(open) => !open && setFinanceBooking(null)}
        authAxios={authAxios}
        onSaved={load}
      />
    </div>
  );
}

function AddAppointmentDialog({ open, onOpenChange, authAxios, onCreated }) {
  const initial = {
    name: "", phone: "", service: "", preferred_time: "", status: "confirmed",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", notes: "",
  };
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickDate = async (selectedDate, selectedService = form.service) => {
    setDate(selectedDate);
    set("preferred_time", "");
    setSlots([]);
    if (!selectedDate) return;
    setSlotsLoading(true);
    try {
      const response = await authAxios().get("/availability", {
        params: { date: format(selectedDate, "yyyy-MM-dd"), service: selectedService || undefined },
      });
      setSlots(response.data.slots);
    } catch {
      toast.error("Could not load open times.");
    } finally {
      setSlotsLoading(false);
    }
  };

  const submit = async () => {
    if (!form.name || !form.phone || !form.service || !date || !form.preferred_time) {
      toast.error("Name, phone, service, date and time are required.");
      return;
    }
    setSaving(true);
    try {
      await authAxios().post("/admin/bookings", {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      toast.success("Appointment added");
      setForm(initial);
      setDate(null);
      setSlots([]);
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create appointment");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "bg-[#0a0a0a] border-white/10 text-white placeholder:text-zinc-600 focus:border-white focus-visible:ring-1 focus-visible:ring-white h-11";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-appointment-dialog" className="bg-[#0a0a0a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white">Add Appointment</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Manually add a booking (walk-in, call-in, or manual entry).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-zinc-300 text-xs">Customer Name *</Label>
            <Input data-testid="add-name" className={inputCls} value={form.name}
              onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-zinc-300 text-xs">Phone Number *</Label>
            <Input data-testid="add-phone" className={inputCls} value={form.phone}
              onChange={(e) => set("phone", e.target.value)} placeholder="(510) 555-0000" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-zinc-300 text-xs">Service Type *</Label>
            <Select value={form.service} onValueChange={(v) => {
              set("service", v);
              if (date) pickDate(date, v);
            }}>
              <SelectTrigger data-testid="add-service" className={inputCls}>
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-white/10 text-white">
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem data-testid={`add-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={s} value={s} className="focus:bg-white/10 focus:text-white">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs">Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="add-date"
                  className={`${inputCls} w-full flex items-center justify-between rounded-md px-3 text-left ${date ? "text-white" : "text-zinc-600"}`}>
                  {date ? format(date, "PP") : "Pick a date"}
                  <CalendarIcon2 size={15} className="text-zinc-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#111111] border-white/10" align="start">
                <Calendar mode="single" selected={date} onSelect={pickDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs">Time *</Label>
            <Select
              value={form.preferred_time}
              onValueChange={(v) => set("preferred_time", v)}
              disabled={!date || slotsLoading}
            >
              <SelectTrigger data-testid="add-time" className={inputCls}>
                <SelectValue placeholder={slotsLoading ? "Loading..." : "Select time"} />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-white/10 text-white">
                {slots.filter((slot) => slot.available).map((slot) => (
                  <SelectItem
                    key={slot.time}
                    value={slot.time}
                    data-testid={`add-time-${slot.time.replace(/[:\s]/g, "")}`}
                    className="focus:bg-white/10 focus:text-white"
                  >
                    {slot.time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-zinc-300 text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="add-status" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-white/10 text-white">
                {["new", "confirmed", "completed", "cancelled"].map((s) => (
                  <SelectItem data-testid={`add-status-option-${s}`} key={s} value={s} className="focus:bg-white/10 focus:text-white capitalize">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs">Vehicle Year</Label>
            <Input data-testid="add-vehicle-year" className={inputCls} value={form.vehicle_year}
              onChange={(e) => set("vehicle_year", e.target.value)} placeholder="2022" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs">Make / Model</Label>
            <Input data-testid="add-vehicle-make" className={inputCls} value={form.vehicle_make}
              onChange={(e) => set("vehicle_make", e.target.value)} placeholder="Porsche 911" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-zinc-300 text-xs">Notes</Label>
            <Textarea data-testid="add-notes" className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-zinc-600 min-h-[70px]"
              value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional notes" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button data-testid="add-cancel" type="button" onClick={() => onOpenChange(false)}
            className="rounded-full border border-white/15 text-zinc-300 px-4 py-2 text-sm hover:bg-white/5">
            Cancel
          </button>
          <button data-testid="add-submit" type="button" onClick={submit} disabled={saving}
            className="rounded-full bg-[#fafafa] text-[#050505] font-semibold px-5 py-2 text-sm hover:bg-white disabled:opacity-60 inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Appointment
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true=authed, false=not
  const [token, setTokenState] = useState(localStorage.getItem(TOKEN_KEY));

  const check = useCallback(async () => {
    // Skip if arriving from OAuth callback
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setAuthed(false);
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      if (r.data?.is_admin) setAuthed(true);
      else { setAuthed(false); toast.error("This Google account is not authorized for admin access."); }
    } catch {
      setAuthed(false);
    }
  }, [token]);

  useEffect(() => {
    check();
  }, [check]);

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    setTokenState(null);
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
    setAuthed(false);
  };

  const handleLoggedIn = (newToken) => {
    if (newToken) setTokenState(newToken);
    setAuthed(true);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  return authed ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <LoginForm onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); handleLoggedIn(t); }} />
  );
}
